import { logger } from '@heidi/runtime/logger';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { type PersonalizationDataResponse } from '../../contracts';
import {
  GET_USER_PERSONALISATIONS,
  TOGGLE_PERSONALIZATION_INSIGHT_MUTATION_KEY,
} from '../../contracts/keys';
import { togglePersonalizationInsight } from '../api/user';

export { TOGGLE_PERSONALIZATION_INSIGHT_MUTATION_KEY };

export const useMutateStateTogglePersonalizationInsight = () => {
  const togglePersonalizationInsightMutation = useMutationState({
    filters: { mutationKey: [TOGGLE_PERSONALIZATION_INSIGHT_MUTATION_KEY] },
    select: (mutation) => mutation.state,
  });

  const currentMutation = togglePersonalizationInsightMutation.at(-1);
  const isPending = currentMutation?.status === 'pending';

  return { isPending };
};

export const useMutateTogglePersonalizationInsight = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [TOGGLE_PERSONALIZATION_INSIGHT_MUTATION_KEY],
    mutationFn: togglePersonalizationInsight,
    onMutate: async ({ category, rule, should_use }) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER_PERSONALISATIONS] });

      queryClient.setQueryData<PersonalizationDataResponse>(
        [GET_USER_PERSONALISATIONS],
        (oldData) => {
          if (!oldData) return oldData;

          return produce(oldData, (draft) => {
            const categoryData = draft.personalization_data[category];
            if (!categoryData) return;

            const insightIndex = categoryData.findIndex(
              (insight) => insight.rule === rule
            );
            if (insightIndex === -1) return;

            const insight = categoryData.at(insightIndex);
            if (insight) {
              insight.should_use = should_use;
            }
          });
        }
      );
    },
    onError: (error) => {
      logger.error('Failed to toggle personalization insight', error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_USER_PERSONALISATIONS] });
    },
  });
};
